import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MangaCard from '@/components/feature/MangaCard';
import { mangaList } from '@/mocks/manga';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const category = categories.find((c) => c.slug === slug);

  const filteredManga = useMemo(() => {
    let list = slug ? mangaList.filter((m) => m.category === slug) : mangaList;
    switch (sortBy) {
      case 'popular':
        return [...list].sort((a, b) => b.views - a.views);
      case 'rating':
        return [...list].sort((a, b) => b.rating - a.rating);
      default:
        return list;
    }
  }, [slug, sortBy]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name}漫画 - 在线免费阅读 - 073漫画网`,
          description: `073漫画网${category.name}漫画分类，${category.description}。在线免费阅读下拉式${category.name}漫画，操作便捷加载快速。`,
          url: `https://073mh.com/category/${slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '073漫画网',
            url: 'https://073mh.com',
          },
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${category.name}漫画` },
              ]}
            />

            {/* Category Header */}
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  {category.name}漫画
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>

            {/* Sub-category tabs */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm text-foreground-500">排序：</span>
              {([
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '人气' },
                { key: 'rating', label: '评分' },
              ] as const).map((sort) => (
                <button
                  key={sort.key}
                  onClick={() => setSortBy(sort.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    sortBy === sort.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {sort.label}
                </button>
              ))}
            </div>

            {/* Manga Grid */}
            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-10" data-product-shop="">
                {filteredManga.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画</p>
                <Link
                  to="/"
                  className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm"
                >
                  返回首页浏览更多
                </Link>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}